// ─── Footer line rotation — THREE-POOL SYSTEM ──────────────────────
//
// The freemium model (~1 AI tool/suite: a real free daily amount, then
// a fair per-use price — no subscription) makes the strong absolutist
// lines FACTUALLY FALSE on those pages. "No paywall / you're not the
// product" on a page with any paid path is the exact brand-corroding
// contradiction this split prevents.
//
//   Pool A — Safe Anywhere : true on every tool, free or freemium.
//   Pool B — Free-Tool-Only: absolutist; true ONLY on 100%-free tools.
//                            NEVER served on a freemium page.
//   Pool C — Freemium      : honest-about-the-model. ONLY on freemium
//                            tools. HELD BACK — no tool is freemium-
//                            active yet, so C is dormant.
//
//   getFooterLine('free')     → A + B   (default; current behavior)
//   getFooterLine('freemium') → A + C   (never B)
//
// Number policy (locked): may reference suite count (stable), NEVER a
// tool count (always growing → always re-rots). "Suspiciously free"
// removed (plants the doubt the system exists to dispel).
//
// Golden footer logic unchanged (exact rarity, independent roll).
//   Tune as traffic grows: 5,000 → 10,000 → 15,000 → 20,000

const GOLDEN_RARITY = 5000;

// FREEMIUM not yet shipped. Pool C is written/correct/dormant. When the
// model is final, pass 'freemium' from those tools' call sites — nothing
// here changes. Deliberately NO hard "5/day" in user-facing C lines:
// the number may change and we won't ship a claim we might break.

// Weights: 10 = lead lines (shown most) · 5 = support lines.

const POOL_A = [
  { text: 'Made for students, not for shareholders.',                 weight: 10 },
  { text: 'Built for the 2am study session.',                          weight: 10 },
  { text: "Made for students who don't have $19/month.",               weight: 10 },
  { text: 'Powered by spite for bad SaaS.',                            weight: 10 },
  { text: 'Free because school costs enough already.',                 weight: 10 },
  { text: "We built it. You use it. That's the deal.",                 weight: 10 },
  { text: 'Built for studying, not for a pitch deck.',                 weight: 10 },
  { text: 'Fast, honest, and not trying to trick you.',                weight: 10 },
  { text: 'For the assignment due in six hours.',                      weight: 10 },
  { text: 'Made for the library at closing time.',                     weight: 10 },
  { text: 'For students, by someone who remembers being broke.',       weight: 5 },
  { text: 'Built for people without a budget line for "tools."',       weight: 5 },
  { text: 'Built for broke students by a former one.',                 weight: 5 },
  { text: 'No fluff. No onboarding flow. Just the tool.',              weight: 5 },
  { text: "For people who can't expense \"productivity tools.\"",      weight: 5 },
  { text: 'Made for people the app stores overcharge.',                weight: 5 },
  { text: 'For the all-nighter that has no budget.',                   weight: 5 },
  { text: 'Made for the group chat at finals.',                        weight: 5 },
  { text: "Bookmark it or don't. It'll be here either way.",           weight: 5 },
  { text: 'Built to be useful first.',                                 weight: 5 },
  { text: 'If this saved you ten minutes, we did our job.',            weight: 5 },
  { text: 'Made for students who Googled "free" first.',               weight: 5 },
  { text: "For students who deserve nice things that don't cost.",     weight: 5 },
  { text: 'The honest kind of free.',                                  weight: 5 },
  { text: 'Open it, use it, close it. Simple by design.',              weight: 5 },
];

const POOL_B = [
  { text: 'Free, real free, no asterisks.',                            weight: 10 },
  { text: 'Free. We mean it.',                                         weight: 10 },
  { text: 'No paywall. No popup. No "sign in with Google to continue."', weight: 10 },
  { text: "You're the user, not the product.",                         weight: 10 },
  { text: "We don't sell your data. We don't have your data.",         weight: 10 },
  { text: 'No login. No tracker. Just tools.',                         weight: 10 },
  { text: 'No demo. No trial. No catch.',                              weight: 10 },
  { text: 'No "limited time offer." This is the offer.',               weight: 10 },
  { text: 'No account. No catch. No kidding.',                         weight: 10 },
  { text: "No \"upgrade to unlock.\" Nothing's locked here.",          weight: 10 },
  { text: "We don't want your email.",                                 weight: 5 },
  { text: 'The price is zero. The asterisk is decorative.',            weight: 5 },
  { text: 'No login wall. No paywall. No wall.',                       weight: 5 },
  { text: "No data harvested. There's nothing to harvest.",            weight: 5 },
  { text: "No credit card. We wouldn't know what to do with it.",      weight: 5 },
  { text: 'No "5 free uses then." Just uses.',                         weight: 5 },
  { text: 'Nothing here expires.',                                     weight: 5 },
  { text: 'No "contact sales." There is no sales.',                    weight: 5 },
  { text: 'Costs nothing. Tracks nothing. Asks nothing.',              weight: 5 },
  { text: 'No "create a free account." Just free.',                    weight: 5 },
  { text: 'The whole thing, free, no fine print.',                     weight: 5 },
  { text: "No \"your free trial is ending.\" It isn't.",               weight: 5 },
  { text: 'No premium. No plus. No pro. Just this.',                   weight: 5 },
  { text: 'Nothing to cancel. Nothing to remember to cancel.',         weight: 5 },
  { text: 'No upsell mid-task. No upsell at all.',                     weight: 5 },
  { text: "This one's free, full stop.",                               weight: 5 },
];

const POOL_C = [
  { text: 'AI costs real money. A fair price, never a trap.',          weight: 10 },
  { text: "No subscription. A day pass when you need more. That's it.", weight: 10 },
  { text: "We'd rather charge per-use than trap you in a subscription.", weight: 10 },
  { text: 'No "free trial then surprise." The free part is permanent.', weight: 10 },
  { text: 'Pay only the days you actually need more. Nothing recurring.', weight: 10 },
  { text: "The limit isn't a trick to upsell you. It's just what AI costs.", weight: 10 },
  { text: "Most of NerdSuite is free. This tool has real per-use costs; we're upfront.", weight: 10 },
  { text: "We could've hidden the cost in a subscription. We didn't.", weight: 10 },
  { text: "No dark patterns on the paywall. Just: AI is expensive, here's the deal.", weight: 5 },
  { text: 'The paid part funds the AI, not a yacht.',                  weight: 5 },
  { text: 'Honest freemium: a real free amount, a fair price past it, no recurring trap.', weight: 5 },
  { text: 'No streak to lose, no FOMO.',                               weight: 5 },
  { text: "We'll always tell you why the rest costs.",                 weight: 5 },
  { text: "Tokens or a day pass — you pick, you're never auto-charged.", weight: 5 },
  { text: "The free tools stay free. This one's AI, so it's fair-priced past the free part.", weight: 5 },
  { text: 'We charge for the AI because someone has to. Not for anything else.', weight: 5 },
];

const GOLDEN_LINE = "🌟 You got the golden footer. It's rarer than you think.";

function weightedPick(pool) {
  const total = pool.reduce((s, l) => s + l.weight, 0);
  let r = Math.random() * total;
  for (const line of pool) {
    r -= line.weight;
    if (r <= 0) return line.text;
  }
  return pool[pool.length - 1].text;
}

/**
 * Footer line for this page load.
 * @param {'free'|'freemium'} toolType default 'free' (A+B) → every
 *   existing call site unchanged. 'freemium' → A+C, NEVER B.
 */
export function getFooterLine(toolType = 'free') {
  if (Math.random() < 1 / GOLDEN_RARITY) {
    return { text: GOLDEN_LINE, golden: true };
  }
  const pool = toolType === 'freemium'
    ? [...POOL_A, ...POOL_C]
    : [...POOL_A, ...POOL_B];
  return { text: weightedPick(pool), golden: false };
}

/** Debug: force the golden line. */
export function _test_golden() {
  return { text: GOLDEN_LINE, golden: true };
}

/** Debug/introspection: pool sizes (tests use this). */
export function _pools() {
  return { A: POOL_A.length, B: POOL_B.length, C: POOL_C.length };
}
